<template>
  <div class="page-container inventory-report">
    <div class="page-title-container">
      <h1 class="page-title">Inventario</h1>
    </div>

    <!-- Resumen: las cards de estado filtran la tabla -->
    <section class="summary-cards mb-4">
      <div
        v-for="card in cards"
        :key="card.key"
        class="summary-card"
        :class="[
          `summary-card-${card.color}`,
          {
            'summary-card-clickable': card.status,
            'summary-card-active': card.status && activeStatus === card.status,
          },
        ]"
        :role="card.status ? 'button' : null"
        @click="card.status && selectStatus(card.status)"
      >
        <p class="summary-card-label">{{ card.label }}</p>
        <p class="summary-card-value" v-if="card.currency" v-decimal:pen="card.value"></p>
        <p class="summary-card-value" v-else v-int="card.value"></p>
      </div>
    </section>

    <div class="table-container">
      <div class="table-header">
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative"
        >
          <div class="w-full w-md-auto md-flex-1">
            <div
              class="d-flex justify-content-between align-items-center justify-content-lg-start"
            >
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-flex d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="refresh()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>

                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold"
                  v-b-tooltip.hover
                  title="Descargar"
                  @click="download()"
                >
                  <i class="ri-download-2-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section
            class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0"
          >
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold mr-0"
                v-b-tooltip.hover
                title="Recargar"
                @click="refresh()"
              >
                <i class="ri-loop-right-line"></i>
              </button>

              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Descargar"
                @click="download()"
              >
                <i class="ri-download-2-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre o SKU"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="onSetFilters"
          ></filter-menu>
        </div>

        <div
          class="w-100 d-flex flex-wrap align-items-center mt-2"
          v-if="showFiltersList"
        >
          <span
            v-for="(val, name) in filterList"
            v-bind:key="name"
            class="filter-item"
          >
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div>
        <!-- Mobile -->
        <div class="custom-table-mobile d-xl-none">
          <section class="custom-table-mobile-header inventory-mobile-grid">
            <div>
              <span>Producto</span>
            </div>
            <div>
              <span>Stock</span>
            </div>
          </section>

          <div class="custom-table-mobile-body">
            <div class="custom-table-mobile-row" v-for="o in list" :key="o.id">
              <div class="inventory-mobile-grid">
                <div class="text-left">
                  <router-link :to="{ name: 'products-edit', params: { id: o.id } }">
                    {{ o.name || "-" }}
                  </router-link>
                  <p class="m-0 text-muted">{{ o.code }}</p>
                </div>

                <div class="text-center">
                  <span class="stock-value" :class="`stock-${getStockLevel(o)}`">{{ o.stock }}</span>
                </div>

                <div>
                  <button
                    type="button"
                    class="border-0 bg-transparent"
                    @click="o.showMobileData = !o.showMobileData"
                  >
                    <span v-if="!o.showMobileData">
                      <i class="ri-arrow-down-s-line"></i>
                    </span>
                    <span v-else>
                      <i class="ri-arrow-up-s-line"></i>
                    </span>
                  </button>
                </div>
              </div>

              <div class="pt-3" v-if="o.showMobileData">
                <ul class="list-unstyled p-0">
                  <li class="mb-1">
                    <span class="font-weight-bold d-inline-block mr-2">Umbral:</span>
                    <span>{{ getThreshold(o) }}</span>
                  </li>
                  <li class="mb-1">
                    <span class="font-weight-bold d-inline-block mr-2">Venta sin stock:</span>
                    <i class="ri-check-line text-success" v-if="o.allowNegativeStock"></i>
                    <span v-else>-</span>
                  </li>
                  <li class="mb-1">
                    <span class="font-weight-bold d-inline-block mr-2">Costo unitario:</span>
                    <span v-decimal:pen="o.cost"></span>
                  </li>
                  <li>
                    <span class="font-weight-bold d-inline-block mr-2">Valor en stock:</span>
                    <span v-decimal:pen="getStockValue(o)" v-if="getStockValue(o) !== null"></span>
                    <span v-else>-</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop -->
        <table class="custom-table w-100 d-none d-xl-table">
          <thead>
            <tr>
              <th class="pl-md-4 text-left">Producto</th>
              <th>SKU</th>
              <th>Stock actual</th>
              <th>Umbral</th>
              <th>
                Venta sin <br />
                stock
              </th>
              <th>
                Costo <br />
                unitario
              </th>
              <th class="pr-md-4">
                Valor en <br />
                stock
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="pl-md-4 product-name">
                <router-link :to="{ name: 'products-edit', params: { id: o.id } }">
                  {{ o.name || "-" }}
                </router-link>
              </td>
              <td class="text-center">{{ o.code }}</td>
              <td class="text-center">
                <span class="stock-value" :class="`stock-${getStockLevel(o)}`">{{ o.stock }}</span>
              </td>
              <td class="text-center">{{ getThreshold(o) }}</td>
              <td class="text-center">
                <i class="ri-check-line text-success check-icon" v-if="o.allowNegativeStock"></i>
                <span v-else>-</span>
              </td>
              <td class="text-center" v-decimal:pen="o.cost"></td>
              <td class="text-center pr-md-4">
                <span v-decimal:pen="getStockValue(o)" v-if="getStockValue(o) !== null"></span>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="table-footer d-flex justify-content-end align-items-center"
      >
        <pagination
          :total="total"
          :page="page"
          :page-size="pageSize"
          :pages="pages"
          :changePage="changePage"
          :changePageSize="changePageSize"
          v-if="list.length"
        />

        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fileDownload from "js-file-download";

import {
  Alert,
  Constants,
  FilterMenu,
  FilterMixin,
  PaginationMixin,
  SearchBar,
  Util,
} from "@wizepe/wize-admin";

import { ProductService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Umbral por defecto del backend si el resumen aún no cargó
const DEFAULT_LOW_STOCK_THRESHOLD = 5;

export default {
  components: {
    SearchBar,
    FilterMenu,
  },
  mixins: [PaginationMixin, FilterMixin],
  data() {
    return {
      list: [],
      summary: null,
      filterOptions: [
        {
          label: "Estado de stock",
          key: "stockStatus",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.StockStatus.IN_STOCK,
            LocalConstants.StockStatus.LOW_STOCK,
            LocalConstants.StockStatus.OUT_OF_STOCK,
            LocalConstants.StockStatus.NEGATIVE_STOCK,
          ],
        },
      ],
    };
  },
  computed: {
    activeStatus() {
      return this.filters.stockStatus || null;
    },
    globalThreshold() {
      return this.summary ? this.summary.lowStockThreshold : DEFAULT_LOW_STOCK_THRESHOLD;
    },
    cards() {
      const s = this.summary || {};
      return [
        { key: "total", label: "Total de productos", value: s.totalProducts || 0, color: "neutral" },
        { key: "in", label: "En stock", value: s.inStock || 0, color: "green", status: "IN_STOCK" },
        { key: "low", label: "Stock bajo", value: s.lowStock || 0, color: "yellow", status: "LOW_STOCK" },
        { key: "out", label: "Agotados", value: s.outOfStock || 0, color: "red", status: "OUT_OF_STOCK" },
        { key: "negative", label: "Stock negativo", value: s.negativeStock || 0, color: "dark-red", status: "NEGATIVE_STOCK" },
        { key: "value", label: "Valor del inventario", value: s.inventoryValue || 0, color: "neutral", currency: true },
      ];
    },
  },
  methods: {
    getParams() {
      // Solo productos activos, igual que el resumen
      return {
        search: this.search,
        status: LocalConstants.ProductsStatus.ACTIVE.key,
        ...this.filters,
      };
    },
    async loadSummary() {
      try {
        const response = await ProductService.getInventorySummary();
        this.summary = response.payload;
      } catch (error) {
        console.error(error);
        Alert.error(error.errors ? error.errors.message : "No se pudo cargar el resumen de inventario");
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = {
          page: this.page - 1,
          size: this.pageSize,
          sort: "stock,asc",
          ...this.getParams(),
        };
        const response = await ProductService.list(params);

        this.list = response.payload.content.map((item) => {
          return {
            ...item,
            showMobileData: false,
          };
        });

        this.total = response.payload.totalElements;
        this.pages = response.payload.totalPages;
      } catch (error) {
        console.error(error);
        Alert.error(error.errors ? error.errors.message : "No se pudo cargar el inventario");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async refresh() {
      await Promise.all([this.loadSummary(), this.loadData()]);
    },
    // Card de estado: filtra la tabla; si ya estaba activa, quita el filtro
    selectStatus(status) {
      const filters = { ...this.filters };
      if (filters.stockStatus === status) {
        delete filters.stockStatus;
      } else {
        filters.stockStatus = status;
      }
      this.onSetFilters(filters);
    },
    onSetFilters(filters) {
      this.page = 1;
      this.setFilters(filters);
    },
    async download() {
      try {
        await this.$store.dispatch("app/loading", true);
        const response = await ProductService.downloadProductList(this.getParams());
        fileDownload(response.data, Util.getFileName(response));
      } catch (error) {
        console.error(error);
        Alert.error(error.errors ? error.errors.message : "No se pudo descargar el archivo");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    getStockLevel(o) {
      const stock = Number(o.stock);
      if (stock < 0 && o.allowNegativeStock) return "negative";
      if (stock <= 0 && !o.allowNegativeStock) return "out";
      if (stock <= 0) return "neutral"; // sin stock pero con venta sin stock
      if (o.lowStock) return "low";
      return "ok";
    },
    getThreshold(o) {
      return o.lowStockThreshold !== null && o.lowStockThreshold !== undefined
        ? o.lowStockThreshold
        : `Global (${this.globalThreshold})`;
    },
    getStockValue(o) {
      const stock = Number(o.stock);
      if (!(stock > 0) || o.cost === null || o.cost === undefined) return null;
      return stock * Number(o.cost);
    },
  },
  async mounted() {
    await this.refresh();
  },
};
</script>

<style lang="stylus" scoped>
.inventory-report
  .summary-cards
    display: grid
    grid-template-columns: repeat(3, 1fr)
    gap: 12px

    @media (min-width: 1200px)
      grid-template-columns: repeat(6, 1fr)

  .summary-card
    background-color: #fff
    border: 1px solid #e3e5ea
    border-left-width: 4px
    border-radius: 8px
    padding: 12px 14px
    min-width: 0
    transition: box-shadow 0.15s, border-color 0.15s

    &-label
      margin: 0 0 6px
      font-size: 12px
      color: #6c757d
      line-height: 1.2

    &-value
      margin: 0
      font-size: 20px
      font-weight: 700
      color: #3e4657
      word-break: break-word

    &-neutral
      border-left-color: #adb5bd

    &-green
      border-left-color: #28a745

      .summary-card-value
        color: #1e7e34

    &-yellow
      border-left-color: #f0ad00

      .summary-card-value
        color: #b07f00

    &-red
      border-left-color: #dc3545

      .summary-card-value
        color: #c82333

    &-dark-red
      border-left-color: #8b0000

      .summary-card-value
        color: #8b0000

    &-clickable
      cursor: pointer

      &:hover
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08)

    &-active
      box-shadow: 0 0 0 2px rgba(62, 70, 87, 0.35)

  @media (max-width: 575px)
    .summary-card
      padding: 10px

      &-value
        font-size: 15px

  .product-name
    max-width: 320px

  .stock-value
    display: inline-block
    min-width: 36px
    padding: 2px 8px
    border-radius: 10px
    font-weight: 600

  .stock-ok
    color: #1e7e34
    background-color: #e6f4ea

  .stock-low
    color: #8a6500
    background-color: #fff4cc

  .stock-out
    color: #c82333
    background-color: #fdecee

  .stock-negative
    color: #fff
    background-color: #8b0000

  .stock-neutral
    color: #6c757d
    background-color: #f0f1f3

  .check-icon
    font-size: 18px

  .inventory-mobile-grid
    display: grid
    grid-template-columns: 1fr 80px 40px
    align-items: center
</style>
